import { AnyGalleryEntityVersion, Collections } from '@uploadcam/common';
import {
  CollectionReference,
  FieldValue,
  Firestore,
  Timestamp,
  deleteField,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { getCollectionRef } from '../../../../@harmony/angular';

export interface UpdateGalleryData {
  title?: string;
  description?: string;
  /**
   * Password change is a single semantic update — both fields go together:
   * - Pass both as strings (raw + pre-hashed) to set a new password.
   * - Pass both as `null` to remove the password.
   * - Leave both `undefined` to leave the password untouched.
   * Setting just one of them is a misuse and the server rule will reject it.
   */
  publicPassword?: string | null;
  publicPasswordHash?: string | null;
  itemIds?: string[];
  /** `undefined` leaves it untouched; an array (possibly empty) replaces the field. */
  contributorUserIds?: string[];
  /** `undefined` leaves it untouched; a boolean replaces (use `false` to disable). */
  isOpenToTeam?: boolean;
  /** `undefined` leaves it untouched; `null` clears; a Date sets a new expiration. */
  expiresAt?: Date | null;
}

export class UpdateGalleryInteractor {
  private readonly collection: CollectionReference<AnyGalleryEntityVersion>;

  constructor(private readonly db: Firestore) {
    this.collection = getCollectionRef(this.db, Collections.Galleries);
  }

  public async execute(galleryId: string, data: UpdateGalleryData): Promise<void> {
    const patch: { [k: string]: string | string[] | boolean | Timestamp | FieldValue } = {
      updatedAt: Timestamp.now(),
    };

    if (data.title !== undefined) patch['title'] = data.title.trim();
    if (data.description !== undefined) {
      const trimmed = data.description.trim();
      patch['description'] = trimmed.length > 0 ? trimmed : deleteField();
    }
    if (data.publicPasswordHash !== undefined) {
      patch['publicPasswordHash'] =
        data.publicPasswordHash === null ? deleteField() : data.publicPasswordHash;
    }
    if (data.publicPassword !== undefined) {
      patch['publicPassword'] = data.publicPassword === null ? deleteField() : data.publicPassword;
    }
    if (data.itemIds !== undefined) patch['itemIds'] = data.itemIds;
    if (data.contributorUserIds !== undefined) {
      patch['contributorUserIds'] =
        data.contributorUserIds.length > 0 ? data.contributorUserIds : deleteField();
    }
    if (data.isOpenToTeam !== undefined) {
      patch['isOpenToTeam'] = data.isOpenToTeam ? true : deleteField();
    }
    if (data.expiresAt !== undefined) {
      patch['expiresAt'] =
        data.expiresAt === null ? deleteField() : Timestamp.fromDate(data.expiresAt);
    }

    await updateDoc(doc(this.collection, galleryId), patch);
  }
}
